import React from 'react';
import { Button, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { styles } from './select-styles';
import theme from '../../theme';

export type MenuItemType = {
  label: string | null | number;
  value: string | number;
  disabled?: boolean;
};

export type SelectProps = {
  items?: MenuItemType[];
  value?: string;
  name?: string;
  placeholder?: string;
  height?: string;
  onChange?: (newValue: any) => void;
  classes?: any;
  dataTestId?: string;
  disabled?: boolean;
  isMenuButton?: boolean;
};

export const Select = ({
  classes,
  dataTestId,
  disabled,
  isMenuButton,
  onChange,
  items = [],
  name = '',
  placeholder = 'Select',
  value = '',
  height,
  ...props
}: SelectProps) => {
  const generateDisplayedValue = (val: string) => {
    const selectedItem = items?.find(item => val === item.value);
    if (selectedItem) {
      return selectedItem.label;
    }
    if (val.length > 35) {
      let result = val.substring(0, 30) + '...';
      return result;
    }
    return val;
  };

  const buttonHeight = height || 'auto';
  return (
    <Menu autoSelect={false} strategy="fixed">
      <MenuButton
        height={buttonHeight}
        as={Button}
        disabled={disabled}
        sx={
          classes?.button ??
          (value ? styles.buttonSelected : styles.button)
        }
        rightIcon={<ChevronDownIcon sx={classes?.icon ?? styles.icon} />}
        data-testid={dataTestId?.trim() ?? 'dropdown'}
        _focus={{ boxShadow: 'none' }}
        _expanded={{
          bg: isMenuButton
            ? theme.colors.accent[100]
            : theme.colors.text['light'],
          border: isMenuButton
            ? `${theme.borders['1px']} ${theme.colors.accent[100]}`
            : `${theme.borders['1px']} ${theme.colors.foundation[500]}`
        }}
        _hover={{
          border: isMenuButton
            ? `${theme.borders['1px']} ${theme.colors.accent[100]}`
            : `${theme.borders['1px']} ${theme.colors.foundation[400]}`,
          bg: isMenuButton
            ? theme.colors.accent[100]
            : theme.colors.text['light']
        }}>
        {generateDisplayedValue(value) || placeholder}
      </MenuButton>
      <Portal appendToParentPortal={false}>
        <MenuList sx={{ ...styles.list, zIndex: 5000 }}>
          {items?.map((item, key) => (
            <MenuItem
              sx={
                item?.value === value || item.label === value
                  ? styles.itemSelected
                  : styles.item
              }
              style={{
                minHeight: '30px',
                fontSize: '14.5px'
              }}
              key={key}
              value={item?.value}
              name={name}
              onClick={onChange}
              data-testid={item?.value?.toString().trim()}>
              {item?.label}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};

Select.displayName = 'Select';
